import React, {
  createContext,
  useContext, //useEffect,
  ReactNode,
  useEffect,
} from 'react'
import { useRouter } from 'next/router'
import { useWebAuthentication } from './useWebAuthentication'
import { useRememberLastOrganisation } from './useLastOrganisation'
import { setupWebMFAInterceptor } from './setupWebMFAInterceptor'
import { MFAModal } from '@modules/mfa/MFAModal'
import { Box } from '@common/fundamentals'

// Create context for web authentication
const WebAuthContext = createContext<ReturnType<
  typeof useWebAuthentication
> | null>(null)

interface WebAuthProviderProps {
  children: ReactNode
}

/**
 * Web Authentication Provider
 *
 * This provider:
 * 1. Initializes the useWebAuthentication hook
 * 2. Sets up MFA interceptors for API calls
 * 3. Provides authentication state to the entire app
 * 4. Records the organisation the user is visiting for the next login
 */
export const WebAuthProvider: React.FC<WebAuthProviderProps> = ({
  children,
}) => {
  const webAuth = useWebAuthentication()
  const { mfaStatus } = webAuth
  const router = useRouter()

  useRememberLastOrganisation(webAuth.user, router)

  // Set up MFA interceptors when the provider mounts
  useEffect(() => {
    // Only set up interceptors in browser environment
    if (typeof window !== 'undefined') {
      setupWebMFAInterceptor({
        setMFARequired: webAuth.setMFARequired,
        setMFAExpired: webAuth.setMFAExpired,
      })
    }
  }, [])

  return (
    <WebAuthContext.Provider value={webAuth}>
      <Box display={mfaStatus === 'expired' ? 'none' : 'block'}>{children}</Box>

      <MFAModal open={mfaStatus === 'expired'} />
    </WebAuthContext.Provider>
  )
}

/**
 * Hook to use the web authentication context
 *
 * @returns The web authentication state and actions
 */
export const useWebAuthContext = () => {
  const context = useContext(WebAuthContext)

  if (!context) {
    throw new Error('useWebAuthContext must be used within a WebAuthProvider')
  }

  return context
}
